/**
 * Evaluations API Client
 *
 * API functions for evaluation management.
 */

import { apiRequest } from './api';

// Types
export type EvaluationStatus = 'draft' | 'submitted' | 'in_review' | 'final' | 'cancelled';
export type AgreementType = 'development' | 'training' | 'target' | 'other';

export type ScoringModel = 'tasks_v1' | 'core_tasks_v2';

export interface Evaluation {
  id: number;
  employeeId: number;
  managerId: number;
  periodeStart: string;
  periodeEnd: string;
  status: EvaluationStatus;
  taskScore: string | null;
  competencyScore: string | null;
  totalScore: string | null;
  opmerkingen?: string | null; // General remarks from manager (Step 6)
  selfEvaluationAt: string | null;
  submittedAt: string | null;
  finalizedAt: string | null;
  createdAt: string;
  updatedAt: string;
  // Names from list endpoint
  employeeName?: string | null;
  managerName?: string | null;
  // Epic 4 — Beoordelen op kerntaken (Story 4.1, AD-4). Bepaalt of de wizard
  // Step2Tasks (tasks_v1) of StepKerntaken (core_tasks_v2) toont.
  scoringModel: ScoringModel;
  // Epic 4 — Story 4.4 (AD-5). Alleen gezet ná finalize van een
  // core_tasks_v2-beoordeling.
  packageSnapshot?: string | null;
}

export interface SelfEvaluationTask {
  taskId: number;
  score: number;
  toelichting: string | null;
}

export interface TaskScore {
  taskId: number;
  score: number;
  toelichting?: string | null;
  selectedAgreementTypes?: number[];
  agreementDescription?: string;
}

/**
 * Epic 4 — Beoordelen op kerntaken (Story 4.2). `periodId` is verplicht
 * (AD-6: core_task_scores.period_id is NOT NULL).
 */
export interface CoreTaskScore {
  periodId: number;
  coreTaskId: number;
  score: number;
  toelichting?: string | null;
  selectedAgreementTypes?: number[];
}

/**
 * Epic 4 — Beoordelen op kerntaken (Story 4.5, AC #1). Bewust GEEN
 * `toelichting` (AC #0 legt de kolommen van `core_task_self_evaluations`
 * letterlijk vast, zonder toelichting-veld).
 */
export interface CoreTaskSelfEvaluationScore {
  periodId: number;
  coreTaskId: number;
  score: number;
}

export interface CompetencyScore {
  competencyId: number;
  score: number;
  toelichting?: string | null;
  selectedAgreementTypes?: number[];
  agreementDescription?: string;
}

export interface Agreement {
  id: number;
  evaluationId: number;
  type: AgreementType;
  beschrijving: string;
  deadline: string | null;
  isCompleted: boolean;
  isEmployeeActionHolder: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface WizardState {
  evaluationId: number;
  currentStep: number;
  stepData: Record<string, unknown>;
  lastActiveAt: string | null;
}

export interface HRAnnotation {
  id: number;
  evaluationId: number;
  content: string;
  createdBy: number;
  createdAt: string;
  updatedAt: string;
}

export interface EvaluationWithDetails extends Evaluation {
  employee: {
    id: number;
    userId: number;
    functie: string | null;
    team: string | null;
    name?: string | null;
  } | null;
  managerName?: string | null;
  selfEvaluations: SelfEvaluationTask[];
  taskScores: TaskScore[];
  competencyScores: CompetencyScore[];
  agreements: Agreement[];
  wizardState: WizardState | null;
  hrAnnotations?: HRAnnotation[];
  // Extended score fields for 3-part scoring system
  vakCompetencyScore?: string | null;
  basisCompetencyScore?: string | null;
  // Opmerkingen is inherited from Evaluation interface
}

export interface ScoreCalculation {
  taskScore: number;
  vakCompetencyScore: number;
  basisCompetencyScore: number;
  competencyScore?: number; // Legacy field for backwards compatibility
  totalScore: number;
  totalScoreLevel?: number; // 1-5 score level for overall classification
  totalScoreLabel?: string; // "Onvoldoende", "Matig", "Voldoende", "Goed", "Uitzonderlijk"
  weights: {
    task: number;
    vakCompetency: number;
    basisCompetency: number;
  };
  // Epic 4 (Story 4.3, AC #2) — alleen gezet voor core_tasks_v2-beoordelingen;
  // >=2 entries betekent >=2 gedekte tijdvakken (Step5Review toont dan de
  // uitgesplitste totaalregel).
  corePeriodBreakdown?: Array<{
    periodId: number;
    weight: number;
    months: number;
    blockPoints: number;
  }>;
  breakdown: {
    tasks: Array<{
      taskId: number;
      name: string;
      score: number;
      maxPoints: number;
      earnedPoints: number;
      weging?: number; // Legacy field
      weighted?: number; // Legacy field
    }>;
    vakCompetencies: Array<{
      id: number;
      name: string;
      taskName: string | null;
      score: number;
      maxPoints: number;
      earnedPoints: number;
    }>;
    basisCompetencies: Array<{
      id: number;
      name: string;
      score: number;
      maxPoints: number;
      earnedPoints: number;
    }>;
    // Legacy field for backwards compatibility
    competencies?: {
      basis: Array<{ id: number; name: string; score: number }>;
      taak: Array<{ id: number; name: string; taskName: string | null; score: number }>;
    };
  };
}

export interface EvaluationListResponse {
  data: Evaluation[];
  total: number;
}

export interface CreateEvaluationInput {
  employeeId: number;
  periodeStart: string;
  periodeEnd: string;
}

export interface SelfEvaluationInput {
  taskId: number;
  score: number;
  toelichting?: string;
}

export interface AgreementInput {
  type: AgreementType;
  beschrijving: string;
  deadline?: string;
  isEmployeeActionHolder?: boolean;
}

// API functions
export const evaluationsApi = {
  /**
   * List evaluations
   */
  async list(params?: {
    employeeId?: number;
    managerId?: number;
    status?: EvaluationStatus;
  }): Promise<EvaluationListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.employeeId) searchParams.set('employeeId', params.employeeId.toString());
    if (params?.managerId) searchParams.set('managerId', params.managerId.toString());
    if (params?.status) searchParams.set('status', params.status);

    const query = searchParams.toString();
    return apiRequest<EvaluationListResponse>(`/evaluations${query ? `?${query}` : ''}`);
  },

  /**
   * Get evaluation by ID
   */
  async getById(id: number): Promise<EvaluationWithDetails> {
    return apiRequest<EvaluationWithDetails>(`/evaluations/${id}`);
  },

  /**
   * Create evaluation
   */
  async create(input: CreateEvaluationInput): Promise<Evaluation> {
    return apiRequest<Evaluation>('/evaluations', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  /**
   * Update evaluation status
   * @param opmerkingen - Optional general remarks from manager (saved on submit)
   */
  async updateStatus(id: number, status: EvaluationStatus, opmerkingen?: string): Promise<Evaluation> {
    return apiRequest<Evaluation>(`/evaluations/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status, opmerkingen }),
    });
  },

  /**
   * Delete evaluation (draft only)
   */
  async delete(id: number): Promise<void> {
    await apiRequest(`/evaluations/${id}`, { method: 'DELETE' });
  },

  // Self-evaluation functions

  /**
   * Get self-evaluation
   */
  async getSelfEvaluation(evaluationId: number): Promise<{
    evaluationId: number;
    tasks: SelfEvaluationTask[];
  }> {
    return apiRequest(`/evaluations/${evaluationId}/self-evaluation`);
  },

  /**
   * Save self-evaluation
   */
  async saveSelfEvaluation(
    evaluationId: number,
    tasks: SelfEvaluationInput[]
  ): Promise<{ evaluationId: number; saved: number }> {
    return apiRequest(`/evaluations/${evaluationId}/self-evaluation`, {
      method: 'PUT',
      body: JSON.stringify({ tasks }),
    });
  },

  /**
   * Submit self-evaluation
   */
  async submitSelfEvaluation(evaluationId: number): Promise<Evaluation> {
    return apiRequest(`/evaluations/${evaluationId}/self-evaluation/submit`, {
      method: 'POST',
    });
  },

  // Epic 4 — Beoordelen op kerntaken (Story 4.5, AC #1): core_tasks_v2
  // zelfevaluatie — eigen endpoints, zie evaluations.ts routes.

  /** Get core-task self-evaluation */
  async getCoreTaskSelfEvaluation(evaluationId: number): Promise<{
    evaluationId: number;
    scores: CoreTaskSelfEvaluationScore[];
  }> {
    return apiRequest(`/evaluations/${evaluationId}/core-task-self-evaluation`);
  },

  /** Save core-task self-evaluation */
  async saveCoreTaskSelfEvaluation(
    evaluationId: number,
    scores: CoreTaskSelfEvaluationScore[]
  ): Promise<{ evaluationId: number; saved: number }> {
    return apiRequest(`/evaluations/${evaluationId}/core-task-self-evaluation`, {
      method: 'PUT',
      body: JSON.stringify({ scores }),
    });
  },

  /** Submit core-task self-evaluation */
  async submitCoreTaskSelfEvaluation(evaluationId: number): Promise<Evaluation> {
    return apiRequest(`/evaluations/${evaluationId}/core-task-self-evaluation/submit`, {
      method: 'POST',
    });
  },

  // Wizard functions

  /**
   * Get wizard state
   */
  async getWizardState(evaluationId: number): Promise<WizardState> {
    return apiRequest(`/evaluations/${evaluationId}/wizard`);
  },

  /**
   * Save wizard state
   */
  async saveWizardState(
    evaluationId: number,
    currentStep: number,
    stepData: Record<string, unknown>
  ): Promise<WizardState> {
    return apiRequest(`/evaluations/${evaluationId}/wizard`, {
      method: 'PUT',
      body: JSON.stringify({ currentStep, stepData }),
    });
  },

  // Scoring functions

  /**
   * Save task scores
   */
  async saveTaskScores(
    evaluationId: number,
    scores: TaskScore[]
  ): Promise<{ evaluationId: number; saved: number }> {
    return apiRequest(`/evaluations/${evaluationId}/task-scores`, {
      method: 'PUT',
      body: JSON.stringify({ scores }),
    });
  },

  /**
   * Save core-task scores (Epic 4 — Beoordelen op kerntaken, Story 4.2, core_tasks_v2)
   */
  async saveCoreTaskScores(
    evaluationId: number,
    scores: CoreTaskScore[]
  ): Promise<{ evaluationId: number; saved: number }> {
    return apiRequest(`/evaluations/${evaluationId}/core-task-scores`, {
      method: 'PUT',
      body: JSON.stringify({ scores }),
    });
  },

  /**
   * Save competency scores
   */
  async saveCompetencyScores(
    evaluationId: number,
    scores: CompetencyScore[]
  ): Promise<{ evaluationId: number; saved: number }> {
    return apiRequest(`/evaluations/${evaluationId}/competency-scores`, {
      method: 'PUT',
      body: JSON.stringify({ scores }),
    });
  },

  /**
   * Calculate scores
   */
  async calculateScores(evaluationId: number): Promise<ScoreCalculation> {
    return apiRequest(`/evaluations/${evaluationId}/calculate`);
  },

  // Agreement functions

  /**
   * Get agreements
   */
  async getAgreements(evaluationId: number): Promise<{
    evaluationId: number;
    agreements: Agreement[];
  }> {
    return apiRequest(`/evaluations/${evaluationId}/agreements`);
  },

  /**
   * Add agreement
   */
  async addAgreement(evaluationId: number, input: AgreementInput): Promise<Agreement> {
    return apiRequest(`/evaluations/${evaluationId}/agreements`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  /**
   * Update agreement
   */
  async updateAgreement(
    evaluationId: number,
    agreementId: number,
    input: Partial<AgreementInput & { isCompleted: boolean }>
  ): Promise<Agreement> {
    return apiRequest(`/evaluations/${evaluationId}/agreements/${agreementId}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },

  /**
   * Delete agreement
   */
  async deleteAgreement(evaluationId: number, agreementId: number): Promise<void> {
    await apiRequest(`/evaluations/${evaluationId}/agreements/${agreementId}`, {
      method: 'DELETE',
    });
  },

  // Score Agreements (Task/Competency specific)

  /**
   * Get score agreements for an evaluation
   */
  async getScoreAgreements(evaluationId: number): Promise<{ data: ScoreAgreement[] }> {
    return apiRequest(`/evaluations/${evaluationId}/score-agreements`);
  },

  /**
   * Get score agreements for an employee
   */
  async getScoreAgreementsForEmployee(employeeId: number): Promise<{ data: ScoreAgreement[] }> {
    return apiRequest(`/evaluations/employee/${employeeId}/score-agreements`);
  },

  /**
   * Create score agreement
   */
  async createScoreAgreement(
    evaluationId: number,
    input: ScoreAgreementInput
  ): Promise<ScoreAgreement> {
    return apiRequest(`/evaluations/${evaluationId}/score-agreements`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  /**
   * Update score agreement
   */
  async updateScoreAgreement(
    evaluationId: number,
    agreementId: number,
    input: Partial<Pick<ScoreAgreementInput, 'beschrijving'> & { status: ScoreAgreementStatus }>
  ): Promise<ScoreAgreement> {
    return apiRequest(`/evaluations/${evaluationId}/score-agreements/${agreementId}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },

  /**
   * Delete score agreement
   */
  async deleteScoreAgreement(evaluationId: number, agreementId: number): Promise<void> {
    await apiRequest(`/evaluations/${evaluationId}/score-agreements/${agreementId}`, {
      method: 'DELETE',
    });
  },
};

// Score Agreement types
export type ScoreAgreementStatus = 'concept' | 'definitief' | 'afgerond' | 'geannuleerd';

export interface ScoreAgreement {
  id: number;
  evaluationId: number | null; // Nullable for standalone agreements
  employeeId: number | null; // For standalone agreements
  taskScoreId: number | null;
  competencyScoreId: number | null;
  agreementTypeId: number;
  beschrijving: string;
  status: ScoreAgreementStatus;
  deadline: string | null; // Optional deadline
  isEmployeeActionHolder: boolean; // Whether employee is responsible for follow-up
  datumVastgelegd: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: number; // User ID of creator
  agreementTypeName?: string;
  agreementTypeIcon?: string;
  agreementTypeColor?: string;
  taskName?: string;
  competencyName?: string;
  employeeName?: string;
  evaluationPeriod?: { start: string; end: string };
  isStandalone?: boolean; // True if not linked to an evaluation
  updates?: AgreementUpdate[]; // Progress updates
  // Archive fields (for soft delete)
  isArchived?: boolean;
  archivedAt?: string;
  archivedBy?: number;
  archivedReason?: string;
  archivedByName?: string;
  // For archived view - agreement type info
  type?: string;
}

// Agreement Update types
export type AgreementUpdateType = 'voortgang' | 'status_wijziging' | 'notitie';
export type AuthorRole = 'medewerker' | 'beoordelaar' | 'hr' | 'admin';

export interface AgreementUpdate {
  id: number;
  agreementId: number;
  authorId: number;
  authorRole: AuthorRole;
  authorName?: string;
  updateType: AgreementUpdateType;
  content: string;
  previousStatus: ScoreAgreementStatus | null;
  newStatus: ScoreAgreementStatus | null;
  // Attachment info
  attachmentName?: string | null;
  attachmentType?: string | null;
  attachmentSize?: number | null;
  hasAttachment?: boolean;
  createdAt: string;
}

export interface AgreementUpdateInput {
  updateType: AgreementUpdateType;
  content: string;
  newStatus?: ScoreAgreementStatus;
  // Attachment (optional, max 3MB)
  attachmentName?: string;
  attachmentType?: string;
  attachmentSize?: number;
  attachmentData?: string; // Base64 encoded
}

export interface ScoreAgreementInput {
  agreementTypeId: number;
  beschrijving: string;
  taskScoreId?: number;
  competencyScoreId?: number;
  isEmployeeActionHolder?: boolean;
}

export interface StandaloneAgreementInput {
  agreementTypeId: number;
  beschrijving: string;
  deadline?: string;
  status?: ScoreAgreementStatus;
  isEmployeeActionHolder?: boolean;
}

// Standalone Agreement API functions
export const standaloneAgreementsApi = {
  /**
   * Create standalone agreement for an employee
   */
  async create(employeeId: number, input: StandaloneAgreementInput): Promise<ScoreAgreement> {
    return apiRequest(`/evaluations/employee/${employeeId}/agreements`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  /**
   * Update standalone agreement
   */
  async update(
    employeeId: number,
    agreementId: number,
    input: Partial<StandaloneAgreementInput & { status: ScoreAgreementStatus }>
  ): Promise<ScoreAgreement> {
    return apiRequest(`/evaluations/employee/${employeeId}/agreements/${agreementId}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },

  /**
   * Delete standalone agreement
   */
  async delete(employeeId: number, agreementId: number): Promise<void> {
    await apiRequest(`/evaluations/employee/${employeeId}/agreements/${agreementId}`, {
      method: 'DELETE',
    });
  },
};

// Agreement Actions API - For action holder agreements and progress tracking
export const agreementActionsApi = {
  /**
   * Get action holder agreements for an employee
   * Returns open agreements where employee is responsible for follow-up
   */
  async getActionHolderAgreements(employeeId: number): Promise<{ data: ScoreAgreement[] }> {
    return apiRequest(`/agreements/action-holder/${employeeId}`);
  },

  /**
   * Get team agreements for a manager
   * Returns all open agreements for the manager's team members
   */
  async getTeamAgreements(): Promise<{ data: ScoreAgreement[] }> {
    return apiRequest('/agreements/team');
  },

  /**
   * Get a single agreement with all updates
   */
  async getAgreementWithUpdates(agreementId: number): Promise<{ data: ScoreAgreement }> {
    return apiRequest(`/agreements/score/${agreementId}`);
  },

  /**
   * Add a progress update to an agreement
   */
  async addUpdate(agreementId: number, input: AgreementUpdateInput): Promise<{ data: AgreementUpdate }> {
    return apiRequest(`/agreements/score/${agreementId}/updates`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
};

// Assessment Report types and API
export interface AssessmentReportItem {
  category: 'taak' | 'vakcompetentie' | 'basiscompetentie';
  name: string;
  score: number;
  description: string;
}

export interface AssessmentReportAgreement {
  type: string;
  beschrijving: string;
  deadline: string | null;
  context: string;
}

export interface AssessmentReport {
  watGingGoed: {
    summary: string;
    items: AssessmentReportItem[];
  };
  watGingMinder: {
    summary: string;
    items: AssessmentReportItem[];
  };
  afspraken: {
    summary: string;
    items: AssessmentReportAgreement[];
  };
  overallConclusion: string;
}

export interface GenerateReportInput {
  transcript?: string;
}

export const assessmentReportApi = {
  /**
   * Generate assessment report JSON
   */
  async generate(evaluationId: number, input?: GenerateReportInput): Promise<{ report: AssessmentReport }> {
    return apiRequest(`/evaluations/${evaluationId}/assessment-report`, {
      method: 'POST',
      body: JSON.stringify(input || {}),
    });
  },

  /**
   * Generate assessment report HTML (returns raw HTML string)
   */
  async generateHtml(evaluationId: number, input?: GenerateReportInput): Promise<string> {
    const response = await fetch(
      `${import.meta.env.VITE_API_BASE || '/api/v1'}/evaluations/${evaluationId}/assessment-report/html`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
        body: JSON.stringify(input || {}),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to generate report HTML');
    }

    return response.text();
  },
};

// Assessment Reports (Beoordelingsverslagen) types and API
export type AssessmentReportStatus = 'concept' | 'ingediend' | 'definitief';

export interface SavedAssessmentReport {
  id: number;
  employeeId: number;
  evaluationId: number | null;
  periodeStart: string;
  periodeEnd: string;
  status: AssessmentReportStatus;
  htmlContent: string;
  transcript: string | null;
  createdAt: string;
  createdBy: number;
  updatedAt: string;
  finalizedAt: string | null;
  finalizedBy: number | null;
  finalAssessment: string | null;
  employeeName?: string;
  createdByName?: string;
  finalizedByName?: string;
}

export interface CreateAssessmentReportInput {
  employeeId: number;
  evaluationId?: number;
  periodeStart: string;
  periodeEnd: string;
  htmlContent: string;
  transcript?: string;
}

export interface UpdateAssessmentReportInput {
  htmlContent?: string;
  transcript?: string;
  finalAssessment?: string | null;
}

export const savedAssessmentReportsApi = {
  /**
   * List assessment reports for an employee
   */
  async list(employeeId: number): Promise<{ data: SavedAssessmentReport[] }> {
    return apiRequest<{ data: SavedAssessmentReport[] }>(`/assessment-reports?employeeId=${employeeId}`);
  },

  /**
   * List all assessment reports with optional filters
   */
  async listAll(filters?: {
    status?: AssessmentReportStatus;
    year?: number;
  }): Promise<{ data: SavedAssessmentReport[] }> {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.year) params.set('year', filters.year.toString());
    const query = params.toString();
    return apiRequest<{ data: SavedAssessmentReport[] }>(
      `/assessment-reports${query ? `?${query}` : ''}`
    );
  },

  /**
   * Get a single assessment report
   */
  async get(id: number): Promise<{ data: SavedAssessmentReport }> {
    return apiRequest<{ data: SavedAssessmentReport }>(`/assessment-reports/${id}`);
  },

  /**
   * Create a new assessment report
   */
  async create(input: CreateAssessmentReportInput): Promise<{ data: SavedAssessmentReport }> {
    return apiRequest<{ data: SavedAssessmentReport }>('/assessment-reports', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  /**
   * Update assessment report content
   */
  async update(id: number, input: UpdateAssessmentReportInput): Promise<{ data: SavedAssessmentReport }> {
    return apiRequest<{ data: SavedAssessmentReport }>(`/assessment-reports/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },

  /**
   * Update assessment report status
   */
  async updateStatus(id: number, status: AssessmentReportStatus): Promise<{ data: SavedAssessmentReport }> {
    return apiRequest<{ data: SavedAssessmentReport }>(`/assessment-reports/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  },

  /**
   * Delete assessment report
   */
  async delete(id: number): Promise<void> {
    await apiRequest(`/assessment-reports/${id}`, { method: 'DELETE' });
  },

  /**
   * AI edit assessment report content (does NOT save)
   */
  async aiEdit(id: number, instruction: string): Promise<{ data: { htmlContent: string } }> {
    return apiRequest<{ data: { htmlContent: string } }>(`/assessment-reports/${id}/ai-edit`, {
      method: 'POST',
      body: JSON.stringify({ instruction }),
    });
  },
};
